import React, { useEffect, useRef, useState } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew'
import { getBoardInfo, updateBoardRelays } from '../services/authApi'
import './DeviceControlsModal.css'

function unwrapBoardInfo(boardInfo) {
  if (!boardInfo || typeof boardInfo !== 'object') {
    return {}
  }

  if (boardInfo.success !== undefined && boardInfo.data && typeof boardInfo.data === 'object') {
    return boardInfo.data
  }

  return boardInfo
}

function getControlPayload(boardInfo) {
  const payload = unwrapBoardInfo(boardInfo)

  return payload?.config?.control
    || payload?.config?.Control
    || payload?.control
    || payload?.controls
    || payload?.data?.control
    || payload?.data?.controls
    || {}
}

function getRelayValue(boardInfo, relayKey) {
  const control = getControlPayload(boardInfo)
  const relayName = relayKey === 'rly1' ? 'relay1' : 'relay2'
  const relay = control?.relay?.[relayKey] ?? control[relayKey] ?? control[relayName] ?? control[relayName.toUpperCase()]
  const value = typeof relay === 'object' ? relay.state : relay

  return {
    enabled: value === true || value === 1 || String(value).toLowerCase() === 'true' || String(value) === '1',
    label: typeof relay === 'object' ? relay.label || `Relay ${relayKey === 'rly1' ? '1' : '2'}` : `Relay ${relayKey === 'rly1' ? '1' : '2'}`,
  }
}

function getDisplayDeviceId(deviceId, boardInfo) {
  const payload = unwrapBoardInfo(boardInfo)

  return payload?.config?.Device?.ID || payload?.data?.DeviceID || payload?.data?.deviceId || deviceId
}

function RelayControl({ relay, onChange, disabled }) {
  return (
    <article className="device-controls-modal__relay">
      <div className="device-controls-modal__relay-copy">
        <PowerSettingsNewIcon aria-hidden="true" />
        <div>
          <h3>{relay.label}</h3>
          <p>{relay.enabled ? 'On' : 'Off'}</p>
        </div>
      </div>
      <label className="device-controls-modal__switch" aria-label={`${relay.label} state`}>
        <input
          type="checkbox"
          checked={relay.enabled}
          onChange={(event) => onChange(event.target.checked)}
          disabled={disabled}
        />
        <span></span>
      </label>
    </article>
  )
}

function DeviceControlsModal({ deviceId, isOpen, onClose }) {
  const [boardInfo, setBoardInfo] = useState(null)
  const [relayStates, setRelayStates] = useState({
    relay1: { enabled: false, label: 'Relay 1' },
    relay2: { enabled: false, label: 'Relay 2' },
  })
  const [status, setStatus] = useState('idle')
  const [controlError, setControlError] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const closeButtonRef = useRef(null)

  useEffect(() => {
    if (!isOpen) {
      return undefined
    }

    const previousOverflow = document.body.style.overflow
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose?.()
      }
    }

    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', handleKeyDown)
    window.setTimeout(() => closeButtonRef.current?.focus(), 0)

    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose])

  useEffect(() => {
    if (!isOpen || !deviceId) {
      return undefined
    }

    let isActive = true
    setBoardInfo(null)
    setControlError('')
    setStatus('loading')

    getBoardInfo(deviceId)
      .then((response) => {
        if (!isActive) {
          return
        }

        setBoardInfo(response)
        setRelayStates({
          relay1: getRelayValue(response, 'rly1'),
          relay2: getRelayValue(response, 'rly2'),
        })
        setStatus('ready')
      })
      .catch((error) => {
        console.error(`Failed to load controls for ${deviceId}:`, error)

        if (isActive) {
          setBoardInfo(null)
          setStatus('error')
        }
      })

    return () => {
      isActive = false
    }
  }, [deviceId, isOpen])

  if (!isOpen || !deviceId) {
    return null
  }

  const handleBackdropMouseDown = (event) => {
    if (event.target === event.currentTarget) {
      onClose?.()
    }
  }

  const displayDeviceId = getDisplayDeviceId(deviceId, boardInfo)
  const handleRelayToggle = async (relayKey, enabled) => {
    const previousStates = relayStates
    const nextStates = {
      ...relayStates,
      [relayKey]: {
        ...relayStates[relayKey],
        enabled,
      },
    }

    setRelayStates(nextStates)
    setControlError('')
    setIsSaving(true)

    try {
      await updateBoardRelays(deviceId, nextStates)
    } catch (error) {
      setRelayStates(previousStates)
      setControlError(error.message || 'Unable to update the relay controls.')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="device-controls-modal" role="presentation" onMouseDown={handleBackdropMouseDown}>
      <section className="device-controls-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="device-controls-modal-title">
        <header className="device-controls-modal__header">
          <div>
            <span>CNERGI Device Controls</span>
            <h2 id="device-controls-modal-title">Controls</h2>
            <p>Device ID: {displayDeviceId}</p>
          </div>
          <button className="device-controls-modal__close" type="button" aria-label="Close controls" onClick={onClose} ref={closeButtonRef}>
            <CloseIcon aria-hidden="true" />
          </button>
        </header>

        <div className="device-controls-modal__body">
          {status === 'loading' && <div className="device-controls-modal__status">Loading controls...</div>}
          {status === 'error' && <div className="device-controls-modal__status">Unable to load controls for {deviceId}.</div>}
          {status === 'ready' && (
            <div className="device-controls-modal__relays">
              <RelayControl
                relay={relayStates.relay1}
                onChange={(enabled) => handleRelayToggle('relay1', enabled)}
                disabled={isSaving}
              />
              <RelayControl
                relay={relayStates.relay2}
                onChange={(enabled) => handleRelayToggle('relay2', enabled)}
                disabled={isSaving}
              />
              {controlError ? (
                <div className="device-controls-modal__error" role="alert">{controlError}</div>
              ) : null}
            </div>
          )}
        </div>
      </section>
    </div>
  )
}

export default DeviceControlsModal
